/* =============================================================
   PartnerProfile.jsx — Partner university profile (IO admin)
   -------------------------------------------------------------
   Layout is IDENTICAL to StudentUniversity.jsx.
   The only difference: an "Edit Profile" button lets the IO
   edit all fields inline. Read mode = exactly what students see.

   Sections:
     • Left card (col-span-7): blue header, exchange details,
                                at-a-glance stats, who to contact
     • Right top (col-span-5): Useful Links
     • Right bottom (col-span-5): Key Dates

   In edit mode the left card gains extra "Partnership Settings"
   fields (name, country, type, connection, abbreviation) that
   are admin-only and hidden in read mode.

   Connected partners (linked_university_id is set):
     - Exchange details, contacts, links and dates are READ-ONLY
       (data comes from the linked university's own Settings).
     - Only Partnership Settings remain editable.
     - Name field in Partnership Settings has an autocomplete that
       searches Unixplore universities — allows re-linking or
       linking an existing Manual partner.
   ============================================================= */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams }      from 'react-router-dom';
import Layout             from '../components/Layout';
import { supabase }       from '../lib/supabase';
import { usePartnerById } from '../hooks/usePartnerById';

/* ─── CONSTANTS ──────────────────────────────────────────────── */
const COLOR_CLASSES = {
  red:     { bg: 'bg-red-100 text-red-700',         dot: 'bg-red-400'     },
  amber:   { bg: 'bg-amber-100 text-amber-700',     dot: 'bg-amber-400'   },
  blue:    { bg: 'bg-blue-100 text-blue-700',        dot: 'bg-blue-400'    },
  indigo:  { bg: 'bg-indigo-100 text-indigo-700',   dot: 'bg-indigo-400'  },
  slate:   { bg: 'bg-slate-100 text-slate-600',     dot: 'bg-slate-400'   },
  emerald: { bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
  violet:  { bg: 'bg-violet-100 text-violet-700',   dot: 'bg-violet-400'  },
  rose:    { bg: 'bg-rose-100 text-rose-700',       dot: 'bg-rose-400'    },
};
const COLOR_OPTIONS = ['red','amber','blue','indigo','slate','emerald','violet','rose'];

const COUNTRIES = [
  '🇩🇪 Germany', '🇫🇷 France', '🇪🇸 Spain', '🇮🇹 Italy', '🇳🇱 Netherlands',
  '🇸🇪 Sweden', '🇩🇰 Denmark', '🇵🇱 Poland', '🇵🇹 Portugal', '🇧🇪 Belgium',
  '🇬🇧 UK', '🇺🇸 USA', '🇨🇳 China', '🇯🇵 Japan', '🇰🇷 South Korea',
  '🇮🇳 India', '🇧🇷 Brazil', '🇦🇺 Australia', '🇨🇦 Canada', '🇸🇬 Singapore',
  '🇨🇭 Switzerland', '🇦🇹 Austria', '🇨🇿 Czech Republic', '🇭🇺 Hungary',
  '🇳🇴 Norway', '🇫🇮 Finland', '🇮🇪 Ireland', '🇬🇷 Greece',
];

const CONN_STYLE = {
  Connected:      { badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  'Guest Portal': { badge: 'bg-sky-100 text-sky-700',         dot: 'bg-sky-500'     },
  Manual:         { badge: 'bg-slate-100 text-slate-500',     dot: ''               },
};

/* ─── SMALL HELPERS ──────────────────────────────────────────── */
function EInput({ label, value, onChange, type = 'text', placeholder, className = '' }) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">{label}</label>}
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] transition"
      />
    </div>
  );
}

function ESelect({ label, value, onChange, options, className = '' }) {
  return (
    <div className={className}>
      {label && <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">{label}</label>}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] cursor-pointer"
      >
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}


/* ─── COMPONENT ──────────────────────────────────────────────── */
export default function PartnerProfile() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const { partner, loading, patchPartner } = usePartnerById(id);

  const [editing,   setEditing]   = useState(false);
  const [form,      setForm]      = useState({});
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState('');

  /* ── Autocomplete state (for name field in Partnership Settings) */
  const [suggestions,     setSuggestions]     = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [acSearching,     setAcSearching]     = useState(false);
  const acTimeout  = useRef(null);
  const nameRef    = useRef(null);

  /* ── Populate form when partner loads ────────────────────── */
  useEffect(() => {
    if (!partner) return;
    setForm({
      name:                    partner.name                    ?? '',
      abbreviation:            partner.abbreviation            ?? '',
      country:                 partner.country                 ?? '',
      type:                    partner.type                    ?? 'BILATERAL',
      connection:              partner.connection              ?? 'Manual',
      contact_emails:          partner.contact_emails          ?? [],
      campus:                  partner.campus                  ?? '',
      business_school:         partner.business_school         ?? '',
      language_of_instruction: partner.language_of_instruction ?? '',
      exchange_period:         partner.exchange_period         ?? '',
      useful_links:            partner.useful_links            ?? [],
      key_dates:               partner.key_dates               ?? [],
      linkedUniversityId:      partner.linked_university_id   ?? null,
    });
  }, [partner]);

  const set = k => v => setForm(f => ({ ...f, [k]: v }));

  /* ── Derived: is this a Connected (linked) partner? ─────── */
  // Reactive: uses form.linkedUniversityId so it updates immediately
  // when the admin links/unlinks during edit.
  const isLinked = !!form.linkedUniversityId;

  /* ── Debounced autocomplete search on name field ─────────── */
  useEffect(() => {
    if (!editing) return;

    const q = (form.name ?? '').trim();

    // Don't search when already linked or name too short
    if (isLinked || q.length < 2) {
      clearTimeout(acTimeout.current);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    clearTimeout(acTimeout.current);
    acTimeout.current = setTimeout(async () => {
      setAcSearching(true);
      const { data } = await supabase
        .from('universities')
        .select('id, name, country, abbreviation')
        .ilike('name', `%${q}%`)
        .limit(6);
      const results = data ?? [];
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setAcSearching(false);
    }, 300);

    return () => clearTimeout(acTimeout.current);
  }, [form.name, isLinked, editing]);

  /* ── Select a university from the autocomplete dropdown ─── */
  const selectLinkedUniversity = (uni) => {
    setForm(f => ({
      ...f,
      name:               uni.name,
      abbreviation:       uni.abbreviation
                            ?? uni.name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase(),
      country:            uni.country ?? f.country,
      connection:         'Connected',
      linkedUniversityId: uni.id,
    }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  /* ── Remove the Unixplore link ───────────────────────────── */
  const clearLinkedUniversity = () => {
    setForm(f => ({ ...f, connection: 'Manual', linkedUniversityId: null }));
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  /* ── Contacts ───────────────────────────────────────────── */
  const addContact    = ()       => setForm(f => ({ ...f, contact_emails: [...f.contact_emails, { name: '', email: '', role: '' }] }));
  const removeContact = i        => setForm(f => ({ ...f, contact_emails: f.contact_emails.filter((_, j) => j !== i) }));
  const updateContact = (i,k,v)  => setForm(f => ({ ...f, contact_emails: f.contact_emails.map((c,j) => j===i ? {...c,[k]:v} : c) }));

  /* ── Useful links ────────────────────────────────────────── */
  const addLink    = ()       => setForm(f => ({ ...f, useful_links: [...f.useful_links, { label: '', url: '' }] }));
  const removeLink = i        => setForm(f => ({ ...f, useful_links: f.useful_links.filter((_, j) => j !== i) }));
  const updateLink = (i,k,v)  => setForm(f => ({ ...f, useful_links: f.useful_links.map((l,j) => j===i ? {...l,[k]:v} : l) }));

  /* ── Key dates ───────────────────────────────────────────── */
  const addDate    = ()       => setForm(f => ({ ...f, key_dates: [...f.key_dates, { date: '', label: '', color: 'indigo' }] }));
  const removeDate = i        => setForm(f => ({ ...f, key_dates: f.key_dates.filter((_, j) => j !== i) }));
  const updateDate = (i,k,v)  => setForm(f => ({ ...f, key_dates: f.key_dates.map((d,j) => j===i ? {...d,[k]:v} : d) }));

  /* ── Save ────────────────────────────────────────────────── */
  // Uses direct fetch() to bypass GoTrueClient internal lock.
  // For Connected partners we only save Partnership Settings
  // (name, abbreviation, country, type, connection,
  //  linked_university_id). Profile fields (links, dates, etc.)
  // come from the linked university's Settings and must not be
  // overwritten here.
  const save = async () => {
    setSaving(true); setSaveError('');
    try {
      // Base payload — always saved
      const payload = {
        name:                 form.name.trim(),
        abbreviation:         form.abbreviation.trim()  || null,
        country:              form.country.trim(),
        type:                 form.type,
        connection:           isLinked ? 'Connected' : form.connection,
        linked_university_id: form.linkedUniversityId   ?? null,
      };

      // Only save profile fields for non-linked (Manual) partners
      if (!isLinked) {
        Object.assign(payload, {
          contact_emails:          form.contact_emails.filter(c => c.email),
          campus:                  form.campus.trim()                  || null,
          business_school:         form.business_school.trim()         || null,
          language_of_instruction: form.language_of_instruction.trim() || null,
          exchange_period:         form.exchange_period.trim()         || null,
          useful_links:            form.useful_links.filter(l => l.label || l.url),
          key_dates:               form.key_dates.filter(d => d.date || d.label),
        });
      }

      const { data: { session } } = await supabase.auth.getSession();
      const jwt = session?.access_token;
      if (!jwt) throw new Error('Session expired — please refresh the page and log in again.');

      const supabaseUrl  = supabase.supabaseUrl;
      const supabaseAnon = supabase.supabaseKey;
      const res = await fetch(
        `${supabaseUrl}/rest/v1/partner_universities?id=eq.${encodeURIComponent(id)}`,
        {
          method:  'PATCH',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${jwt}`,
            'apikey':        supabaseAnon,
            'Prefer':        'return=minimal',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        let msg = `HTTP ${res.status}`;
        try { msg = JSON.parse(text)?.message ?? msg; } catch { /* keep msg */ }
        throw new Error(msg);
      }

      patchPartner(payload);
      setEditing(false);
    } catch (err) {
      setSaveError(err?.message ?? 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Loading / not found ─────────────────────────────────── */
  if (loading) return (
    <Layout>
      <div className="animate-pulse space-y-6 mt-8">
        <div className="h-8 bg-slate-100 rounded w-64" />
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-7 h-[480px] bg-slate-100 rounded-2xl" />
          <div className="col-span-5 space-y-4">
            <div className="h-44 bg-slate-100 rounded-2xl" />
            <div className="h-44 bg-slate-100 rounded-2xl" />
          </div>
        </div>
      </div>
    </Layout>
  );

  if (!partner) return (
    <Layout>
      <div className="text-center py-24">
        <span className="material-symbols-outlined text-slate-200 text-[48px]">account_balance</span>
        <p className="text-slate-400 mt-3">Partner not found.</p>
        <button onClick={() => navigate('/partners')} className="mt-4 text-sm text-indigo-600 hover:underline">Back to Partners</button>
      </div>
    </Layout>
  );

  /* ── Derived display values ──────────────────────────────── */
  const abbr  = partner.abbreviation
    ?? partner.name?.split(' ').filter(w => w.length > 3).map(w => w[0]).join('').slice(0, 3).toUpperCase()
    ?? '?';
  const conn  = CONN_STYLE[partner.connection] ?? CONN_STYLE.Manual;
  const links = partner.useful_links ?? [];
  const dates = partner.key_dates    ?? [];


  return (
    <Layout>
      {/* Breadcrumb */}
      <button
        onClick={() => navigate('/partners')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-6 transition-colors group"
      >
        <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-0.5 transition-transform">arrow_back</span>
        University Partners
      </button>

      {/* Page header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">{partner.name}</h1>
          <p className="text-[#464555] mt-1.5 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-[15px]">location_on</span>
            {partner.country} · {partner.type}
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <span className={`flex items-center gap-1.5 text-xs font-semibold ${conn.badge} px-3 py-1.5 rounded-full`}>
            {conn.dot && <span className={`w-1.5 h-1.5 rounded-full ${conn.dot}`} />}
            {partner.connection}
          </span>
          {!editing ? (
            <>
              <button
                onClick={() => navigate(`/analytics/partner/${id}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-500 hover:bg-slate-50 transition-colors"
              >
                <span className="material-symbols-outlined text-[16px]">history</span>
                History
              </button>
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <span className="material-symbols-outlined text-[16px]">edit</span>
                Edit Profile
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { setEditing(false); setSaveError(''); setSuggestions([]); setShowSuggestions(false); }}
                className="text-sm text-slate-400 hover:text-slate-600 px-3 py-1.5 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-[#4343d5] text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
              >
                {saving
                  ? <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                  : <span className="material-symbols-outlined text-[15px]">save</span>}
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && (
        <p className="mb-4 text-sm text-red-500 flex items-center gap-1.5">
          <span className="material-symbols-outlined text-[14px]">error</span>{saveError}
        </p>
      )}

      {/* ── INCOMPLETE PROFILE BANNER ────────────────────────── */}
      {!editing && !partner.linked_university_id && (() => {
        const missing = [];
        if (!(partner.key_dates    ?? []).length) missing.push({ icon: 'event',    label: 'key dates'    });
        if (!(partner.useful_links ?? []).length) missing.push({ icon: 'link',     label: 'useful links' });
        if (!(partner.contact_emails ?? []).length) missing.push({ icon: 'contacts', label: 'contact info' });
        if (missing.length === 0) return null;

        const specific = {
          'key dates':    'Students won\'t see any deadlines in their portal.',
          'useful links': 'Students have no resources or links for this university.',
          'contact info': 'Students don\'t know who to reach out to.',
        };

        return (
          <div className="mb-6 flex items-start gap-3 px-4 py-3.5 bg-amber-50 border border-amber-200 rounded-xl">
            <span className="material-symbols-outlined text-[18px] text-amber-500 mt-0.5 flex-shrink-0">warning</span>
            <div className="flex-1 min-w-0">
              {missing.length === 1 ? (
                <p className="text-sm font-medium text-amber-800">
                  No {missing[0].label} added —{' '}
                  <span className="font-normal text-amber-700">{specific[missing[0].label]}</span>
                </p>
              ) : (
                <>
                  <p className="text-sm font-medium text-amber-800 mb-1.5">
                    This profile is incomplete — students may have a degraded experience.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {missing.map(m => (
                      <span key={m.label} className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
                        <span className="material-symbols-outlined text-[13px]">{m.icon}</span>
                        Missing {m.label}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setEditing(true)}
              className="flex-shrink-0 text-xs font-semibold text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              Complete profile
            </button>
          </div>
        );
      })()}

      {/* ── MAIN GRID ────────────────────────────────────────── */}
      <div className="grid grid-cols-12 gap-6">

        {/* ── LEFT (col-span-7): unified card ─────────────────── */}
        <div className="col-span-7">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">

            {/* Blue header */}
            <div className="bg-[#4343d5] px-6 py-8 flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold font-display text-xl">{editing ? (form.abbreviation || abbr) : abbr}</span>
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-white">{editing ? (form.name || partner.name) : partner.name}</h2>
                <p className="text-indigo-200 text-sm mt-1 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px]">location_on</span>
                  {editing ? (form.country || partner.country) : partner.country}
                </p>
              </div>
              <span className="ml-auto text-xs font-semibold bg-white/20 text-white px-3 py-1.5 rounded-full">
                {editing ? form.type : partner.type}
              </span>
            </div>

            {/* Exchange Details */}
            <div className="p-6">
              {editing ? (
                isLinked ? (
                  /* Connected: profile data is read-only */
                  <div className="flex items-start gap-3 p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
                    <span className="material-symbols-outlined text-[20px] text-indigo-500 flex-shrink-0 mt-0.5">sync</span>
                    <div>
                      <p className="text-sm font-semibold text-indigo-800">Profile managed by {partner.name}</p>
                      <p className="text-xs text-indigo-600 mt-1 leading-relaxed">
                        Exchange details, contacts, useful links and key dates are synced automatically from their Unixplore account.
                        Only <strong>Partnership Settings</strong> below can be edited here.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-5">
                    <EInput label="Exchange Period"            value={form.exchange_period}         onChange={set('exchange_period')}         placeholder="e.g. Sep 2026 — Feb 2027"              />
                    <EInput label="Campus"                    value={form.campus}                  onChange={set('campus')}                  placeholder="e.g. South Kensington, London"          />
                    <EInput label="Faculty / Business School" value={form.business_school}         onChange={set('business_school')}         placeholder="e.g. Imperial College Business School"  />
                    <EInput label="Language of Instruction"   value={form.language_of_instruction} onChange={set('language_of_instruction')} placeholder="e.g. English"                           />
                  </div>
                )
              ) : (
                <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Exchange Period</p>
                    <p className="text-sm font-medium text-[#0b1c30] flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-[14px] text-slate-400">calendar_month</span>
                      {partner.exchange_period || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Campus</p>
                    <p className="text-sm font-medium text-[#0b1c30]">{partner.campus || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Faculty / Business School</p>
                    <p className="text-sm font-medium text-[#0b1c30]">{partner.business_school || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Language of Instruction</p>
                    <p className="text-sm font-medium text-[#0b1c30]">{partner.language_of_instruction || '—'}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Who to Contact */}
            <div className="border-t border-slate-100">
              <div className="px-6 py-4 flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">contacts</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Who to Contact</h2>
              </div>
              <div className="px-6 pb-6">
                {editing && !isLinked ? (
                  /* Manual partner: editable contacts */
                  <div className="space-y-3">
                    {form.contact_emails.length > 0 && (
                      <div className="flex gap-2 items-center px-0.5">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide pl-3">Name</span>
                          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide pl-3">Email</span>
                          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide pl-3">Role</span>
                        </div>
                        <span className="w-9" />
                      </div>
                    )}
                    {form.contact_emails.map((c, i) => (
                      <div key={i} className="flex gap-2 items-start">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <input value={c.name}  onChange={e => updateContact(i,'name',e.target.value)}  placeholder="Name"                    className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                          <input value={c.email} onChange={e => updateContact(i,'email',e.target.value)} placeholder="email@university.edu" type="email" className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                          <input value={c.role}  onChange={e => updateContact(i,'role',e.target.value)}  placeholder="Role (e.g. Housing)"      className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                        </div>
                        <button onClick={() => removeContact(i)} className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 transition-colors flex-shrink-0">
                          <span className="material-symbols-outlined text-[16px]">delete</span>
                        </button>
                      </div>
                    ))}
                    <button onClick={addContact} className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-1">
                      <span className="material-symbols-outlined text-[16px]">add</span>Add contact
                    </button>
                  </div>
                ) : (partner.contact_emails ?? []).length === 0 ? (
                  <p className="text-sm text-slate-400">No contacts added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {(partner.contact_emails ?? []).map((c, i) => {
                      const initials = (c.name ?? '?').split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
                      return (
                        <div key={i} className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-sm font-semibold flex-shrink-0">{initials}</div>
                          <div>
                            <p className="text-sm font-semibold text-[#0b1c30]">{c.name || 'International Office'}</p>
                            {c.role && <p className="text-xs text-slate-400 mb-1.5">{c.role}</p>}
                            <a href={`mailto:${c.email}?subject=Message from ESMT Berlin International Office`} className="text-sm text-indigo-600 font-medium hover:underline flex items-center gap-1">
                              <span className="material-symbols-outlined text-[14px]">mail</span>{c.email}
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ── Partnership Settings (edit mode only) ─────────── */}
            {editing && (
              <div className="border-t border-slate-100 px-6 py-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Partnership Settings</p>
                <div className="grid grid-cols-2 gap-5">

                  {/* University Name — with autocomplete for linking */}
                  <div className="col-span-2 relative">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">University Name</label>

                    {/* Linked box (shown when currently linked) */}
                    {isLinked && (
                      <div className="flex items-center gap-3 p-3 mb-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <span className="material-symbols-outlined text-[18px] text-emerald-600 flex-shrink-0">link</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-emerald-700">Linked to Unixplore</p>
                          <p className="text-xs text-emerald-600 mt-0.5 truncate">{form.name}</p>
                        </div>
                        <button
                          onClick={clearLinkedUniversity}
                          className="text-xs text-slate-400 hover:text-red-500 transition-colors underline flex-shrink-0"
                        >
                          Remove link
                        </button>
                      </div>
                    )}

                    {/* Name input — always shown; becomes autocomplete when not linked */}
                    <div className="relative">
                      <input
                        ref={nameRef}
                        type="text"
                        value={form.name}
                        onChange={e => {
                          set('name')(e.target.value);
                          // Typing freely clears any existing link
                          if (form.linkedUniversityId) {
                            setForm(f => ({ ...f, name: e.target.value, linkedUniversityId: null, connection: 'Manual' }));
                          }
                        }}
                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        placeholder="University name"
                        className={`w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition pr-8 ${
                          isLinked ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'
                        }`}
                      />
                      {acSearching && (
                        <svg className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 text-slate-300" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                        </svg>
                      )}
                    </div>

                    {/* Autocomplete dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-4 pt-2.5 pb-1">Found on Unixplore</p>
                        {suggestions.map(uni => (
                          <button
                            key={uni.id}
                            onMouseDown={e => { e.preventDefault(); selectLinkedUniversity(uni); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left"
                          >
                            <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-indigo-600 font-bold text-[11px] font-display">
                                {uni.abbreviation?.slice(0,2) ?? uni.name[0]}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#0b1c30] truncate">{uni.name}</p>
                              <p className="text-xs text-slate-400">{uni.country}</p>
                            </div>
                            <span className="flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full flex-shrink-0">
                              <span className="w-1 h-1 rounded-full bg-emerald-500" />
                              Connect
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    {!isLinked && (
                      <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                        <span className="material-symbols-outlined text-[12px]">info</span>
                        Type to search if this university is on Unixplore and link automatically.
                      </p>
                    )}
                  </div>

                  <EInput   label="Abbreviation" value={form.abbreviation} onChange={set('abbreviation')} placeholder="e.g. CBS" />
                  <div>
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Country</label>
                    <select
                      value={form.country}
                      onChange={e => set('country')(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white text-[#0b1c30] cursor-pointer"
                    >
                      <option value="">Select country</option>
                      {form.country && !COUNTRIES.includes(form.country) && (
                        <option value={form.country}>{form.country}</option>
                      )}
                      {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <ESelect  label="Type" value={form.type} onChange={set('type')} options={['ERASMUS','BILATERAL','DOUBLE DEGREE']} />
                  {/* Connection dropdown — only for non-linked partners */}
                  {!isLinked && (
                    <ESelect label="Connection" value={form.connection} onChange={set('connection')} options={['Manual','Guest Portal']} />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT col-span-5: Links + Dates stacked ─────────── */}
        <div className="col-span-5 flex flex-col gap-6">

          {/* Useful Links */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">link</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Useful Links</h2>
            </div>
            <div className="p-4">
              {/* Only show edit controls for Manual partners */}
              {editing && !isLinked ? (
                <div className="space-y-2.5">
                  {form.useful_links.length > 0 && (
                    <div className="flex gap-1.5 items-center">
                      <span className="w-24 text-xs font-semibold text-slate-400 uppercase tracking-wide pl-2.5">Label</span>
                      <span className="flex-1 text-xs font-semibold text-slate-400 uppercase tracking-wide pl-2.5">URL</span>
                      <span className="w-8" />
                    </div>
                  )}
                  {form.useful_links.map((link, i) => (
                    <div key={i} className="flex gap-1.5 items-center">
                      <input value={link.label} onChange={e => updateLink(i,'label',e.target.value)} placeholder="e.g. Housing Portal"  className="w-24 min-w-0 px-2.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                      <input value={link.url}   onChange={e => updateLink(i,'url',e.target.value)}   placeholder="https://..."           className="flex-1 min-w-0 px-2.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                      <button onClick={() => removeLink(i)} className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 transition-colors flex-shrink-0">
                        <span className="material-symbols-outlined text-[16px]">delete</span>
                      </button>
                    </div>
                  ))}
                  <button onClick={addLink} className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-1">
                    <span className="material-symbols-outlined text-[16px]">add</span>Add link
                  </button>
                </div>
              ) : links.length === 0 ? (
                <p className="text-sm text-slate-400 px-3 py-2">No links added yet.</p>
              ) : (
                <div className="space-y-1">
                  {links.map((link, i) => (
                    <a key={i} href={link.url || '#'} target="_blank" rel="noreferrer"
                      className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 transition-colors group">
                      <span className="material-symbols-outlined text-[18px] text-slate-400 group-hover:text-indigo-500 transition-colors">link</span>
                      <span className="text-sm text-[#464555] group-hover:text-indigo-600 transition-colors">{link.label || link.url}</span>
                      <span className="material-symbols-outlined text-[14px] text-slate-300 ml-auto group-hover:text-indigo-400 transition-colors">open_in_new</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Key Dates */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">event</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Key Dates</h2>
            </div>
            <div className="p-4">
              {editing && !isLinked ? (
                <div className="space-y-2.5">
                  {form.key_dates.length > 0 && (
                    <div className="flex gap-2 items-center px-0.5">
                      <span className="w-20 text-xs font-semibold text-slate-400 uppercase tracking-wide">Date</span>
                      <span className="flex-1 text-xs font-semibold text-slate-400 uppercase tracking-wide">Label</span>
                      <span className="w-24 text-xs font-semibold text-slate-400 uppercase tracking-wide pl-5">Color</span>
                      <span className="w-9" />
                    </div>
                  )}
                  {form.key_dates.map((d, i) => (
                    <div key={i} className="flex gap-2">
                      <input value={d.date}  onChange={e => updateDate(i,'date',e.target.value)}  placeholder="Jun 15"                     className="w-20 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                      <input value={d.label} onChange={e => updateDate(i,'label',e.target.value)} placeholder="e.g. Registration Deadline" className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30]" />
                      <select value={d.color ?? 'indigo'} onChange={e => updateDate(i,'color',e.target.value)}
                        className="w-24 px-2 py-2 text-sm border border-slate-200 rounded-xl bg-white text-[#0b1c30] cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-200">
                        {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
                      </select>
                      <button onClick={() => removeDate(i)} className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 transition-colors flex-shrink-0">
                        <span className="material-symbols-outlined text-[16px]">delete</span>
                      </button>
                    </div>
                  ))}
                  <button onClick={addDate} className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium mt-1">
                    <span className="material-symbols-outlined text-[16px]">add</span>Add date
                  </button>
                </div>
              ) : dates.length === 0 ? (
                <p className="text-sm text-slate-400 px-3 py-2">No key dates added yet.</p>
              ) : (
                <div className="space-y-1">
                  {dates.map((d, i) => {
                    const c = COLOR_CLASSES[d.color] ?? COLOR_CLASSES.indigo;
                    return (
                      <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                        <div className={`flex-shrink-0 whitespace-nowrap text-center text-xs font-bold px-2 py-1 rounded-lg ${c.bg}`}>{d.date}</div>
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
                        <span className="text-sm text-[#464555]">{d.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
}
